// apps/web/src/components/Account/SharedPublicProfileModal.jsx
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import fetchPublicProfileByUserId from '@mysimpledb/shared/data/fetchPublicProfileByUserId';
import { buildAvatarUrl } from '@mysimpledb/shared/utils/mediaUrls';
import {
  createContactRequest,
  fetchConversations,
} from '../../api/messagingClient';
import './SharedPublicProfileModal.css';

function getProfileImage(profile) {
  const storageKey = profile?.useAccountProfilePicture
    ? profile?.accountProfilePicture
    : profile?.profilePicturePath;

  return storageKey ? buildAvatarUrl(storageKey) : null;
}

export default function SharedPublicProfileModal({
  userId,
  token,
  onClose,
  relatedEntryId = null,
  relatedEntryTitle = '',
  onOpenConversation,
}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contactOpen, setContactOpen] = useState(false);
  const [requestType, setRequestType] = useState('other');
  const [messageText, setMessageText] = useState('');
  const [contactError, setContactError] = useState('');
  const [contactSent, setContactSent] = useState(false);
  const [sendingContact, setSendingContact] = useState(false);
  const [existingConversation, setExistingConversation] = useState(null);
  const [checkingConversation, setCheckingConversation] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const result = await fetchPublicProfileByUserId(token, userId);
        if (active) setProfile(result);
      } catch (err) {
        if (active) setError(err.message || 'Unable to load this profile.');
      } finally {
        if (active) setLoading(false);
      }
    };

    if (userId && token) load();

    return () => {
      active = false;
    };
  }, [token, userId]);

  useEffect(() => {
    let active = true;

    const loadExistingConversation = async () => {
      setCheckingConversation(true);

      try {
        const conversations = await fetchConversations();
        const conversation =
          conversations.find(
            (item) =>
              String(item.other_user_id) === String(userId) &&
              ['pending', 'active'].includes(item.status)
          ) || null;

        if (active) setExistingConversation(conversation);
      } catch {
        // Do not block the public profile if conversation lookup fails.
        if (active) setExistingConversation(null);
      } finally {
        if (active) setCheckingConversation(false);
      }
    };

    if (userId) loadExistingConversation();

    return () => {
      active = false;
    };
  }, [userId]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const profileImage = getProfileImage(profile);
  const businessLogo = profile?.businessLogoPath
    ? buildAvatarUrl(profile.businessLogoPath)
    : null;
  const isBusiness = profile?.profileType !== 'personal';
  const hasPreferences =
    profile?.openToQuestions ||
    profile?.openToCollaboration ||
    profile?.openToServiceInquiries;

  const requestOptions = [
    ...(profile?.openToQuestions
      ? [
          {
            value: 'entry_question',
            label: 'Question about a shared entry',
          },
        ]
      : []),
    ...(profile?.openToCollaboration
      ? [{ value: 'collaboration', label: 'Collaboration request' }]
      : []),
    ...(profile?.openToServiceInquiries
      ? [{ value: 'service_inquiry', label: 'Service inquiry' }]
      : []),
    { value: 'other', label: 'Other' },
  ];

  const openExistingConversation = () => {
    if (!existingConversation) return;

    if (onOpenConversation) {
      onOpenConversation(existingConversation.id);
      return;
    }

    sessionStorage.setItem(
      'mysimpledb.openConversationId',
      String(existingConversation.id)
    );
    window.location.assign('/messages');
  };

  const openContactForm = () => {
    if (existingConversation) {
      openExistingConversation();
      return;
    }

    setRequestType(requestOptions[0]?.value || 'other');
    setMessageText('');
    setContactError('');
    setContactSent(false);
    setContactOpen(true);
  };

  const contactButtonLabel = existingConversation
    ? existingConversation.status === 'pending'
      ? 'View Contact Request'
      : `Message ${profile?.displayName || ''}`.trim()
    : `Contact ${profile?.displayName || ''}`.trim();

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    const message = messageText.trim();
    if (!message || sendingContact) return;

    setSendingContact(true);
    setContactError('');

    try {
      const conversation = await createContactRequest({
        recipientUserId: userId,
        requestType,
        messageText: message,
        relatedEntryId,
      });
      setExistingConversation(conversation || null);
      setContactSent(true);
      setMessageText('');
    } catch (err) {
      setContactError(err.message || 'Unable to send contact request.');
    } finally {
      setSendingContact(false);
    }
  };

  return createPortal(
    <div
      className="shared-public-profile-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="shared-public-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="shared-public-profile-title"
      >
        <button
          type="button"
          className="shared-public-profile-close"
          aria-label="Close public profile"
          onClick={onClose}
        >
          ×
        </button>

        {loading && (
          <div className="shared-public-profile-state">Loading profile…</div>
        )}

        {!loading && error && (
          <div className="shared-public-profile-state is-error">
            <h2>Profile unavailable</h2>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && profile && (
          <>
            <header className="shared-public-profile-header">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt=""
                  className="shared-public-profile-avatar"
                />
              ) : (
                <div
                  className="shared-public-profile-avatar-placeholder"
                  aria-hidden="true"
                >
                  {(profile.displayName || '?').charAt(0).toUpperCase()}
                </div>
              )}

              <div className="shared-public-profile-identity">
                <span className="shared-public-profile-label">
                  Public Profile
                </span>
                <h1 id="shared-public-profile-title">
                  {profile.displayName}
                </h1>
                {profile.headline && <p>{profile.headline}</p>}
                {profile.location && (
                  <span className="shared-public-profile-location">
                    {profile.location}
                  </span>
                )}
              </div>
            </header>

            <div className="shared-public-profile-body">
              {profile.bio && (
                <section className="shared-public-profile-section">
                  <h2>About</h2>
                  <p className="shared-public-profile-copy">{profile.bio}</p>
                </section>
              )}

              {isBusiness && profile.businessName && (
                <section className="shared-public-profile-business-card">
                  {businessLogo && (
                    <img
                      src={businessLogo}
                      alt={`${profile.businessName} logo`}
                      className="shared-public-profile-business-logo"
                    />
                  )}
                  <div className="shared-public-profile-business-heading">
                    <div>
                      <span className="shared-public-profile-label">
                        Business
                      </span>
                      <h2>{profile.businessName}</h2>
                    </div>
                    {profile.acceptingClients && (
                      <span className="shared-public-profile-available">
                        Accepting clients
                      </span>
                    )}
                  </div>

                  {profile.businessDescription && (
                    <p className="shared-public-profile-copy">
                      {profile.businessDescription}
                    </p>
                  )}

                  {profile.serviceArea && (
                    <p className="shared-public-profile-business-detail">
                      <strong>Service area:</strong> {profile.serviceArea}
                    </p>
                  )}

                  <div className="shared-public-profile-contact-list">
                    {profile.websiteUrl && (
                      <div className="shared-public-profile-contact-row">
                        <strong>Website:</strong>
                        <a
                          href={profile.websiteUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {profile.websiteUrl}
                        </a>
                      </div>
                    )}
                    {profile.businessEmail && (
                      <div className="shared-public-profile-contact-row">
                        <strong>Email:</strong>
                        <a href={`mailto:${profile.businessEmail}`}>
                          {profile.businessEmail}
                        </a>
                      </div>
                    )}
                    {profile.businessPhoneDisplay && (
                      <div className="shared-public-profile-contact-row">
                        <strong>Phone:</strong>
                        <a
                          href={`tel:${profile.businessPhoneE164}${
                            profile.businessPhoneExtension
                              ? `;ext=${profile.businessPhoneExtension}`
                              : ''
                          }`}
                        >
                          {profile.businessPhoneDisplay}
                          {profile.businessPhoneExtension
                            ? ` ext. ${profile.businessPhoneExtension}`
                            : ''}
                        </a>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {!!profile.skills?.length && (
                <section className="shared-public-profile-section">
                  <h2>Skills & Interests</h2>
                  <div className="shared-public-profile-tags">
                    {profile.skills.map((skill) => (
                      <span key={skill}>{skill}</span>
                    ))}
                  </div>
                </section>
              )}

              {!!profile.links?.length && (
                <section className="shared-public-profile-section">
                  <h2>Links</h2>
                  <div className="shared-public-profile-links">
                    {profile.links.map((link, index) => (
                      <a
                        key={`${link.url}-${index}`}
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {link.label || 'Website'}
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {hasPreferences && (
                <section className="shared-public-profile-section">
                  <h2>Open To</h2>
                  <div className="shared-public-profile-preferences">
                    {profile.openToQuestions && (
                      <span>Questions about shared entries</span>
                    )}
                    {profile.openToCollaboration && (
                      <span>Collaboration requests</span>
                    )}
                    {profile.openToServiceInquiries && (
                      <span>Service inquiries</span>
                    )}
                  </div>
                </section>
              )}

              {profile.allowContactRequests && (
                <section className="shared-public-profile-contact-action">
                  {!contactOpen ? (
                    <>
                      <div>
                        <strong>
                          {existingConversation
                            ? existingConversation.status === 'pending'
                              ? 'Contact request pending'
                              : 'Continue through MySimpleDB'
                            : 'Connect through MySimpleDB'}
                        </strong>
                        <p>
                          {existingConversation
                            ? existingConversation.status === 'pending'
                              ? `Your request to ${profile.displayName} is awaiting a response.`
                              : `Open your existing conversation with ${profile.displayName}.`
                            : 'Send a private contact request without sharing your email address or phone number.'}
                        </p>
                      </div>
                      <button
                        type="button"
                        disabled={checkingConversation}
                        onClick={openContactForm}
                      >
                        {checkingConversation ? 'Checking…' : contactButtonLabel}
                      </button>
                    </>
                  ) : contactSent ? (
                    <div
                      className="shared-public-profile-contact-success"
                      role="status"
                    >
                      <strong>Contact request sent</strong>
                      <p>
                        You can continue the conversation after this user
                        accepts your request.
                      </p>
                    </div>
                  ) : (
                    <form
                      className="shared-public-profile-contact-form"
                      onSubmit={handleContactSubmit}
                    >
                      <div className="shared-public-profile-contact-form-heading">
                        <div>
                          <strong>Contact {profile.displayName}</strong>
                          <p>The recipient can accept or decline your request.</p>
                        </div>
                        <button
                          type="button"
                          className="shared-public-profile-contact-cancel"
                          onClick={() => setContactOpen(false)}
                        >
                          Cancel
                        </button>
                      </div>

                      <label htmlFor="public-profile-request-type">
                        Reason for contacting
                      </label>
                      <select
                        id="public-profile-request-type"
                        value={requestType}
                        onChange={(event) => setRequestType(event.target.value)}
                      >
                        {requestOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      {relatedEntryId && (
                        <div className="shared-public-profile-related-entry">
                          <strong>Related entry:</strong>{' '}
                          {relatedEntryTitle || `Entry #${relatedEntryId}`}
                        </div>
                      )}

                      <label htmlFor="public-profile-contact-message">
                        Introductory message
                      </label>
                      <textarea
                        id="public-profile-contact-message"
                        rows={5}
                        maxLength={2000}
                        value={messageText}
                        placeholder="Briefly explain why you would like to connect…"
                        onChange={(event) => setMessageText(event.target.value)}
                      />
                      <div className="shared-public-profile-contact-form-footer">
                        <span>{messageText.length}/2000</span>
                        <button
                          type="submit"
                          disabled={!messageText.trim() || sendingContact}
                        >
                          {sendingContact ? 'Sending…' : 'Send request'}
                        </button>
                      </div>

                      {contactError && (
                        <div
                          className="shared-public-profile-contact-error"
                          role="alert"
                        >
                          {contactError}
                        </div>
                      )}
                    </form>
                  )}
                </section>
              )}
            </div>
          </>
        )}
      </section>
    </div>,
    document.body
  );
}
